import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
} from '@mui/material';
import { ArrowLeft, Check, Calendar, X, CheckCircle, Pencil } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { appointmentsApi } from '../../../api';
import useUIStore from '../../../store/useUIStore';

const AppointmentDetailPage = ({
  appointmentId = '',
  sess = { uid: '', role: 'user' },
}) => {
  const params = useParams();
  const resolvedAppointmentId = useMemo(
    () => appointmentId || params?.id || '',
    [appointmentId, params?.id]
  );
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const { showSuccess, showError, showInfo } = useUIStore();

  useEffect(() => {
    if (resolvedAppointmentId) {
      fetchAppointmentDetails(resolvedAppointmentId);
    } else {
      setLoading(false);
      setAppointment(null);
    }
  }, [resolvedAppointmentId]);

  const fetchAppointmentDetails = async (id) => {
    try {
      setLoading(true);
      const response = await appointmentsApi.getById(id);
      const data = response?.data?.data || response?.data;
      if (data) {
        setAppointment(data);
      } else {
        showError('Appointment not found');
      }
    } catch (error) {
      console.error('Error fetching appointment:', error);
      showError('Error loading appointment details');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      booked: '#0ea5e9',
      pending: '#ffc107',
      confirmed: '#28a745',
      cancelled: '#dc3545',
      completed: '#6c757d',
    };
    return colors[status] || '#6c757d';
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      const response = await appointmentsApi.update(resolvedAppointmentId, { status: newStatus });
      const result = response?.data || {};
      if (result.success || result.data) {
        showSuccess('Status updated successfully');
        fetchAppointmentDetails();
      } else {
        showError('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showError('Error updating status');
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      showError('Please provide a cancellation reason');
      return;
    }

    try {
      const response = await appointmentsApi.update(resolvedAppointmentId, { status: 'cancelled' });
      const result = response?.data || {};
      if (result.success || result.data) {
        showSuccess('Appointment cancelled successfully');
        setShowCancelModal(false);
        fetchAppointmentDetails();
      } else {
        showError('Failed to cancel appointment');
      }
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      showError('Error cancelling appointment');
    }
  };

  const handleReschedule = async () => {
    if (!newDate || !newTime) {
      showError('Please select both date and time');
      return;
    }

    try {
      showInfo('Rescheduling is not available for this booking flow yet.');
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      showError('Error rescheduling appointment');
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!appointment) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">Appointment not found</Typography>
        <Button sx={{ mt: 2 }} variant="contained" onClick={() => (window.location.href = '/appointments')}>
          Back to Appointments
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <Button variant="text" startIcon={<ArrowLeft />} href="/appointments" sx={{ minWidth: 0, px: 0 }}>
          Back
        </Button>
        <Typography variant="h5">Appointment Details</Typography>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Customer Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={2}>
              <Typography fontWeight={600} sx={{ minWidth: 160 }}>
                Customer Name:
              </Typography>
              <Typography>{appointment.customer_name || '-'}</Typography>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Typography fontWeight={600} sx={{ minWidth: 160 }}>
                Contact Number:
              </Typography>
              <Typography>{appointment.customer_number || '-'}</Typography>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Typography fontWeight={600} sx={{ minWidth: 160 }}>
                Email:
              </Typography>
              <Typography>{appointment.customer_email || '-'}</Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Appointment Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={2}>
              <Typography fontWeight={600} sx={{ minWidth: 160 }}>
                Service:
              </Typography>
              <Typography>{appointment.service || '-'}</Typography>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Typography fontWeight={600} sx={{ minWidth: 160 }}>
                Date &amp; Time:
              </Typography>
              <Typography>{formatDateTime(appointment.appointment_date)}</Typography>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Typography fontWeight={600} sx={{ minWidth: 160 }}>
                Duration:
              </Typography>
              <Typography>{appointment.duration || '-'} minutes</Typography>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Typography fontWeight={600} sx={{ minWidth: 160 }}>
                Employee:
              </Typography>
              <Typography>{appointment.employee_name || '-'}</Typography>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Typography fontWeight={600} sx={{ minWidth: 160 }}>
                Branch:
              </Typography>
              <Typography>{appointment.branch_name || '-'}</Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography fontWeight={600} sx={{ minWidth: 160 }}>
                Status:
              </Typography>
              <Chip
                label={appointment.status || 'pending'}
                sx={{ bgcolor: getStatusColor(appointment.status), color: '#fff' }}
              />
            </Stack>
            {appointment.notes && (
              <Stack direction="row" spacing={2}>
                <Typography fontWeight={600} sx={{ minWidth: 160 }}>
                  Notes:
                </Typography>
                <Typography>{appointment.notes}</Typography>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>

      {appointment.cancel_reason && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Cancellation Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography>{appointment.cancel_reason}</Typography>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Actions
          </Typography>
          <Stack direction="row" flexWrap="wrap" spacing={1} mt={1}>
            {appointment.status === 'pending' && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<Check size={16} />}
                  onClick={() => handleStatusUpdate('confirmed')}
                >
                  Confirm
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<Calendar size={16} />}
                  onClick={() => setShowRescheduleModal(true)}
                >
                  Reschedule
                </Button>
              </>
            )}
            {appointment.status !== 'cancelled' && appointment.status !== 'completed' && (
              <Button
                variant="contained"
                color="error"
                startIcon={<X size={16} />}
                onClick={() => setShowCancelModal(true)}
              >
                Cancel
              </Button>
            )}
            {appointment.status === 'confirmed' && (
              <Button
                variant="contained"
                color="info"
                startIcon={<CheckCircle size={16} />}
                onClick={() => handleStatusUpdate('completed')}
              >
                Mark as Completed
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={showCancelModal} onClose={() => setShowCancelModal(false)} fullWidth maxWidth="sm">
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            minRows={4}
            fullWidth
            label="Cancellation Reason"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Please provide a reason for cancellation..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelModal(false)}>Close</Button>
          <Button color="error" variant="contained" onClick={handleCancel}>
            Confirm Cancellation
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showRescheduleModal} onClose={() => setShowRescheduleModal(false)} fullWidth maxWidth="sm">
        <DialogTitle>Reschedule Appointment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              type="date"
              label="New Date"
              InputLabelProps={{ shrink: true }}
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
            />
            <TextField
              type="time"
              label="New Time"
              InputLabelProps={{ shrink: true }}
              value={newTime}
              onChange={(e) => setNewTime(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRescheduleModal(false)}>Close</Button>
          <Button variant="contained" onClick={handleReschedule}>
            Reschedule
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppointmentDetailPage;
